// Mock feed for the CAM-admin Notifications page (feature spec: Notifications
// (CAM admin)). Each item is one carer action surfacing to the CAM: an action
// completed, a shortlist response, a key document uploaded.
//
// Shape note for promotion: the real model would derive `when` and `group`
// from a createdAt timestamp and carry a per-CAM readAt. Here we use a display
// `when` string and a `read` boolean (prototype-friendly), plus `customer` for
// the global feed's whose-is-this line and `target` for the control-panel tab
// the row deep-links to.

import { type TabId } from '@/cam-admin/control-panel/ControlPanel';

export type CamNotificationType = 'action' | 'shortlist' | 'document';

export type NotificationGroup = 'today' | 'week' | 'earlier';

export type CamNotification = {
  id: string;
  type: CamNotificationType;
  // The event copy, e.g. "Sarah completed 'Review your shortlist'".
  title: string;
  // The care recipient this belongs to — every row carries it, since the feed
  // is global across every customer. The row renders it as "Caring for {name}".
  customer: string;
  when: string;
  read: boolean;
  group: NotificationGroup;
  // Which control-panel tab the deep-link lands on.
  target: TabId;
};

export const CAM_NOTIFICATIONS: CamNotification[] = [
  // The Project MC notifications. Copy is a first draft and the point of having
  // them here is to work on it.
  //
  // All of them go to the care expert on that care recipient, and all of them land
  // on the Shortlists tab of the right circle: a CAM runs several searches at once,
  // so a notification that does not say which one is worse than none.
  //
  // Care secured is the fourth event and it is already below as
  // `c-secured-elsewhere`. It matters twice now, because it is also what expires
  // the payment links still out with providers.
  {
    id: 'c-provider-paid',
    type: 'shortlist',
    title: 'Oakwood care home paid for this lead',
    customer: 'Margarette Qualley',
    when: '20m',
    read: false,
    group: 'today',
    target: 'shortlist',
  },
  {
    id: 'c-provider-declined',
    type: 'shortlist',
    title: 'Riverside lodge can’t take this one',
    customer: 'Margarette Qualley',
    when: '1h',
    read: false,
    group: 'today',
    target: 'shortlist',
  },
  // The backstop, and the one that protects the carer. Publishing no longer
  // guarantees a shortlist, so silence has to reach somebody.
  {
    id: 'c-no-response-3-days',
    type: 'shortlist',
    title: 'No response from 5 providers in 3 days',
    customer: 'Harold Vance',
    when: '3d',
    read: false,
    group: 'week',
    target: 'shortlist',
  },
  {
    id: 'c0',
    type: 'action',
    title: "Sarah completed 'Review your shortlist'",
    customer: 'Margarette Qualley',
    when: '1h',
    read: false,
    group: 'today',
    target: 'actions',
  },
  {
    id: 'c-secured-elsewhere',
    type: 'shortlist',
    title: 'Sophie has secured care elsewhere',
    customer: 'Margarette Qualley',
    when: '2h',
    read: false,
    group: 'today',
    target: 'care-search',
  },
  {
    id: 'c1',
    type: 'shortlist',
    title: 'James secured Oakwood Care Home',
    customer: 'Pedro Qualley',
    when: '3h',
    read: false,
    group: 'today',
    target: 'care-search',
  },
  {
    id: 'c2',
    type: 'document',
    title: "Sarah uploaded 'Financial statements.pdf'",
    customer: 'Margarette Qualley',
    when: '5h',
    read: false,
    group: 'today',
    target: 'documents',
  },
  {
    id: 'c3',
    type: 'shortlist',
    title: 'Aisha commented on Riverside Lodge',
    customer: 'Dorothy Wells',
    when: 'Tue',
    read: true,
    group: 'week',
    target: 'care-search',
  },
  {
    id: 'c4',
    type: 'action',
    title: "Sarah completed 'Upload bank statement'",
    customer: 'Margarette Qualley',
    when: 'Mon',
    read: true,
    group: 'week',
    target: 'actions',
  },
  {
    id: 'c5',
    type: 'document',
    title: "Tom uploaded 'Power of attorney.pdf'",
    customer: 'Harold Reed',
    when: '2 weeks ago',
    read: true,
    group: 'earlier',
    target: 'documents',
  },
  {
    id: 'c6',
    type: 'action',
    title: "James completed 'Book a funding call'",
    customer: 'Pedro Qualley',
    when: '3 weeks ago',
    read: true,
    group: 'earlier',
    target: 'actions',
  },
];
